import { useEffect, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import ConstructionIcon from "@mui/icons-material/Construction";
import HomeIcon from "@mui/icons-material/Home";
import NotificationsIcon from "@mui/icons-material/Notifications";
import ListIcon from "@mui/icons-material/List";
import ImageIcon from "@mui/icons-material/Image";
import { useDispatch, useSelector } from "react-redux";
import { fetchNotification } from "../redux/notificationSlice";
import { useTranslation } from "react-i18next";
import { capitalizeFirstLetter } from "../components/utils/Date";
import { countUnreadAllowedNotifications } from "./notificationUtils";
import { getNotificationPeriodHours } from "./notifications/notificationPeriodUtils";
import { INSTALLATION_LIST_STALE_KEY } from "./installation/installationConfigUtils";
import {
  hasMonitoringModule,
  hasPhotoCaptureModule,
} from "./mobileModuleUtils";

const AppNavBar = ({ navRef }) => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const user = JSON.parse(localStorage.getItem("user")) || null;
  const allNotifications = useSelector((state) => state.notification.data);
  const unreadCount = countUnreadAllowedNotifications(allNotifications, user);
  const { t } = useTranslation();
  const pathSegments = location.pathname.split("/");
  const activeIcon = pathSegments[2];
  const periodHours = getNotificationPeriodHours();

  const showMonitoring = hasMonitoringModule();
  const showPhoto = hasPhotoCaptureModule();

  const tabs = useMemo(() => {
    const items = [
      { id: "home", label: t("Home"), icon: HomeIcon },
      {
        id: "notifications",
        label: t("Notifications"),
        icon: NotificationsIcon,
      },
    ];

    if (showMonitoring) {
      items.push({
        id: "collect",
        label: capitalizeFirstLetter(t("Monitoring")),
        icon: ListIcon,
      });
    }

    if (showPhoto) {
      items.push({ id: "photo", label: t("Photo"), icon: ImageIcon });
    }

    items.push({
      id: "installation",
      label: t("Installation"),
      icon: ConstructionIcon,
    });

    return items;
  }, [showMonitoring, showPhoto, t]);

  const handleNavigation = (icon) => {
    if (icon === "home") {
      history.push("/app/home");
    }
    if (icon === "installation") {
      const shouldRefreshList = sessionStorage.getItem(
        INSTALLATION_LIST_STALE_KEY,
      );
      history.push("/app/installation", {
        refreshInstallationList: shouldRefreshList ? Date.now() : undefined,
      });
    }
    if (icon === "notifications") {
      history.push("/app/notifications");
    }
    if (icon === "collect") {
      history.push("/app/collect");
    }
    if (icon === "photo") {
      history.push("/app/photo");
    }
  };

  const isTabActive = (tabId) => {
    if (tabId === "installation") {
      return (
        activeIcon === "installation" || activeIcon === "installationdetails"
      );
    }
    if (tabId === "notifications") {
      return (
        activeIcon === "notifications" || activeIcon === "notificationsdetails"
      );
    }
    return activeIcon === tabId;
  };

  useEffect(
    () => {
      dispatch(fetchNotification(periodHours));
    },
    // eslint-disable-next-line
    [history, dispatch],
  );

  return (
    <nav className="bottom-nav-bar" ref={navRef} aria-label={t("Navigation")}>
      {tabs.map((tab) => {
        const Icon = tab.icon;
        const isActive = isTabActive(tab.id);

        return (
          <button
            key={tab.id}
            type="button"
            className={`icon-container ${isActive ? "active" : ""}`}
            onClick={() => handleNavigation(tab.id)}
            aria-current={isActive ? "page" : undefined}
          >
            {tab.id === "notifications" ? (
              <span className="notifications-icon-wrapper">
                <Icon />
                {unreadCount > 0 && (
                  <span className="notification-badge-dot" aria-hidden />
                )}
              </span>
            ) : (
              <Icon />
            )}
            <span className="icon-text">{tab.label}</span>
          </button>
        );
      })}
    </nav>
  );
};

export default AppNavBar;
